const LEGACY_VARIABLE_PATTERN = /%([a-z0-9_]+)%/gi;
const TEMPLATE_VARIABLE_PATTERN = /\{\{\s*([a-z0-9_.]+)\s*\}\}/gi;

type VariableContext = Record<string, unknown>;

export interface TemplateVariableContextInput {
  businessName?: string;
  vendorName?: string;
  vendorEmail?: string;
  clientFullName?: string;
  clientEmail?: string;
  clientPhone?: string;
  eventType?: string;
  eventDate?: string | Date | null;
  eventLocation?: string;
  consultDate?: string | Date | null;
  packageName?: string;
  priceTotal?: string | number | null;
  invoiceLink?: string;
  bookingStartTime?: string | Date | null;
  bookingEndTime?: string | Date | null;
  invoiceDueDate?: string | Date | null;
  invoiceBalance?: string | number | null;
  invoiceRetainerAmount?: string | number | null;
  invoiceDiscounts?: string;
  invoicePaymentPlan?: string;
  clientPortalLink?: string;
  extras?: VariableContext;
}

export const TEMPLATE_VARIABLE_KEYS = [
  'client_name',
  'client_full_name',
  'client_email',
  'client_phone',
  'vendor_name',
  'vendor_email',
  'event_type',
  'event_date',
  'event_location',
  'consult_date',
  'package_name',
  'price_total',
  'invoice_link',
  'business_name',
  'booking.start_time',
  'booking.end_time',
  'invoice.due_date',
  'invoice.balance',
  'invoice.retainer_amount',
  'invoice.discounts',
  'invoice.payment_plan',
  'client_portal',
] as const;

const TEMPLATE_VARIABLE_LABELS: Record<(typeof TEMPLATE_VARIABLE_KEYS)[number], string> = {
  client_name: 'Client First Name',
  client_full_name: 'Client Full Name',
  client_email: 'Client Email',
  client_phone: 'Client Phone',
  vendor_name: 'Vendor Name',
  vendor_email: 'Vendor Email',
  event_type: 'Event Type',
  event_date: 'Event Date',
  event_location: 'Event Location',
  consult_date: 'Consult Date',
  package_name: 'Package Name',
  price_total: 'Price Total',
  invoice_link: 'Invoice Link',
  business_name: 'Business Name',
  'booking.start_time': 'Booking Start Time',
  'booking.end_time': 'Booking End Time',
  'invoice.due_date': 'Invoice Due Date',
  'invoice.balance': 'Invoice Balance',
  'invoice.retainer_amount': 'Invoice Retainer Amount',
  'invoice.discounts': 'Invoice Discounts',
  'invoice.payment_plan': 'Invoice Payment Plan',
  client_portal: 'Client Portal Link',
};

export const TEMPLATE_VARIABLE_OPTIONS = TEMPLATE_VARIABLE_KEYS.map(key => ({
  key,
  label: TEMPLATE_VARIABLE_LABELS[key],
}));

export interface TemplateVariableHelpEntry {
  requirement: string;
  fix: string;
}

export const TEMPLATE_VARIABLE_HELP: Record<string, TemplateVariableHelpEntry> = {
  client_name: {
    requirement: 'Client name from selected conversation/contact',
    fix: 'Add a recipient in To or pick a contact with a name.',
  },
  client_full_name: {
    requirement: 'Client full name from selected conversation/contact',
    fix: 'Add a recipient in To or pick a contact with a full name.',
  },
  client_email: {
    requirement: 'Client email',
    fix: 'Add a recipient email in To.',
  },
  client_phone: {
    requirement: 'Client phone from contact/project',
    fix: 'Add client phone in Contact or Project Client Details.',
  },
  vendor_name: {
    requirement: 'Business name',
    fix: 'Set your business name in account/profile settings.',
  },
  vendor_email: {
    requirement: 'Business email',
    fix: 'Set your business email in account/profile settings.',
  },
  business_name: {
    requirement: 'Business name',
    fix: 'Set your business name in account/profile settings.',
  },
  event_type: {
    requirement: 'Project or conversation event type',
    fix: 'Set an event type in project or conversation details.',
  },
  event_date: {
    requirement: 'Project or conversation event date',
    fix: 'Set an event date in project or conversation details.',
  },
  event_location: {
    requirement: 'Event location',
    fix: 'Set an event location in project or conversation details.',
  },
  consult_date: {
    requirement: 'Consult/booking date and time',
    fix: 'Create or link a consult/booking date.',
  },
  package_name: {
    requirement: 'Package/quote selection',
    fix: 'Add package details on the project, quote, or invoice.',
  },
  price_total: {
    requirement: 'Quote or invoice total',
    fix: 'Add total price in quote/invoice data.',
  },
  invoice_link: {
    requirement: 'Invoice URL',
    fix: 'Generate or attach an invoice link before previewing.',
  },
  'booking.start_time': {
    requirement: 'Booking start time',
    fix: 'Create a booking with a start time.',
  },
  'booking.end_time': {
    requirement: 'Booking end time',
    fix: 'Create a booking with an end time.',
  },
  'invoice.due_date': {
    requirement: 'Invoice due date',
    fix: 'Set a due date on the invoice.',
  },
  'invoice.balance': {
    requirement: 'Invoice balance',
    fix: 'Set invoice balance/amount due.',
  },
  'invoice.retainer_amount': {
    requirement: 'Invoice retainer amount',
    fix: 'Set a retainer amount on the invoice.',
  },
  'invoice.discounts': {
    requirement: 'Applied invoice discounts or saved discount templates',
    fix: 'Apply discounts to the invoice or add saved discounts in Invoicing settings.',
  },
  'invoice.payment_plan': {
    requirement: 'Invoice payment plan schedule',
    fix: 'Attach a payment plan to the invoice or configure schedules in Invoicing settings.',
  },
  client_portal: {
    requirement: 'Client portal share link for this project',
    fix: 'Ensure the project has a client portal share token and linked project data.',
  },
};

export const APPOINTMENT_TEMPLATE_VARIABLE_OPTIONS = [
  { key: 'client_name', label: 'Client First Name' },
  { key: 'appointment_date', label: 'Appointment Date' },
  { key: 'appointment_time', label: 'Appointment Time' },
] as const;

export const PAYMENT_TEMPLATE_VARIABLE_OPTIONS = [
  { key: 'client_name', label: 'Client First Name' },
  { key: 'payment_amount', label: 'Payment Amount' },
  { key: 'payment_due_date', label: 'Payment Due Date' },
  { key: 'invoice_link', label: 'Invoice Link' },
] as const;

export const formatTemplateVariableTokens = (options: ReadonlyArray<{ key: string }>): string => {
  return options.map(option => `{{${option.key}}}`).join(', ');
};

const toTrimmedString = (value: unknown): string => {
  if (typeof value === 'string') return value.trim();
  if (value === null || value === undefined) return '';
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
};

const toFormattedDate = (value: unknown): string => {
  if (!value) return '';

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    return value.toLocaleDateString(undefined, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (!trimmedValue) return '';

    const parsed = new Date(trimmedValue);
    if (Number.isNaN(parsed.getTime())) {
      return trimmedValue;
    }

    return parsed.toLocaleDateString(undefined, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }

  return '';
};

const toFormattedDateTime = (value: unknown): string => {
  if (!value) return '';

  const asDate = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(asDate.getTime())) return toTrimmedString(value);

  return asDate.toLocaleString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const toCurrencyString = (value: unknown): string => {
  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (!trimmedValue) return '';

    const parsed = Number(trimmedValue.replace(/[^\d.-]/g, ''));
    if (!Number.isFinite(parsed)) return trimmedValue;

    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 2,
    }).format(parsed);
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 2,
    }).format(value);
  }

  return '';
};

const toSnakeCase = (value: string): string =>
  value
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[-\s]+/g, '_')
    .toLowerCase();

export const extractFirstName = (fullName: string): string => {
  const normalizedName = toTrimmedString(fullName);
  if (!normalizedName) return '';
  return normalizedName.split(/\s+/)[0] || '';
};

export const buildTemplateVariableContext = (
  input: TemplateVariableContextInput = {}
): VariableContext => {
  const clientFullName = toTrimmedString(input.clientFullName);
  const businessName =
    toTrimmedString(input.businessName) || toTrimmedString(input.vendorName) || 'Drahft';

  return {
    client_name: extractFirstName(clientFullName),
    client_full_name: clientFullName,
    client_email: toTrimmedString(input.clientEmail),
    client_phone: toTrimmedString(input.clientPhone),
    vendor_name: toTrimmedString(input.vendorName) || businessName,
    vendor_email: toTrimmedString(input.vendorEmail),
    event_type: toTrimmedString(input.eventType),
    event_date: toFormattedDate(input.eventDate),
    event_location: toTrimmedString(input.eventLocation),
    consult_date: toFormattedDateTime(input.consultDate),
    package_name: toTrimmedString(input.packageName),
    price_total: toCurrencyString(input.priceTotal),
    invoice_link: toTrimmedString(input.invoiceLink),
    business_name: businessName,
    booking: {
      start_time: toFormattedDateTime(input.bookingStartTime),
      end_time: toFormattedDateTime(input.bookingEndTime),
    },
    invoice: {
      due_date: toFormattedDate(input.invoiceDueDate),
      balance: toCurrencyString(input.invoiceBalance),
      retainer_amount: toCurrencyString(input.invoiceRetainerAmount),
      discounts: toTrimmedString(input.invoiceDiscounts),
      payment_plan: toTrimmedString(input.invoicePaymentPlan),
    },
    client_portal: toTrimmedString(input.clientPortalLink),
    ...(input.extras || {}),
  };
};

const getContextValue = (context: Record<string, unknown>, keyPath: string): unknown => {
  if (!keyPath) return undefined;

  return keyPath.split('.').reduce<unknown>((value, key) => {
    if (value && typeof value === 'object' && key in (value as Record<string, unknown>)) {
      return (value as Record<string, unknown>)[key];
    }

    return undefined;
  }, context);
};

export const renderTemplateVariables = (value: string, context: Record<string, unknown> = {}) => {
  if (!value) return value;

  return value.replace(TEMPLATE_VARIABLE_PATTERN, (match, rawKey) => {
    const sourceKey = String(rawKey || '').trim();
    const normalizedKey = sourceKey.toLowerCase();
    const snakeCaseKey = toSnakeCase(sourceKey);

    const candidateKeys = Array.from(new Set([normalizedKey, snakeCaseKey]));
    const resolvedValue = candidateKeys
      .map(key => getContextValue(context, key))
      .find(result => result !== null && result !== undefined && result !== '');

    if (resolvedValue === null || resolvedValue === undefined || resolvedValue === '') {
      return match;
    }

    return String(resolvedValue);
  });
};

export const renderTemplateVariablesInHtml = (html: string, context: Record<string, unknown>) => {
  const container = document.createElement('div');
  container.innerHTML = html;
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    node.textContent = renderTemplateVariables(node.textContent || '', context);
    node = walker.nextNode();
  }
  return container.innerHTML;
};

export const normalizeTemplateVariableSyntax = (value: unknown) => {
  if (typeof value !== 'string' || !value) return value;

  return value.replace(LEGACY_VARIABLE_PATTERN, (_match, rawKey) => {
    return `{{${String(rawKey).trim().toLowerCase()}}}`;
  });
};

export const normalizeTemplateFields = <T extends Record<string, unknown>>(
  template: T,
  fields: string[] = ['subject', 'body', 'message', 'content']
): T => {
  const normalized = { ...template };

  fields.forEach(field => {
    normalized[field as keyof T] = normalizeTemplateVariableSyntax(
      normalized[field as keyof T]
    ) as T[keyof T];
  });

  return normalized;
};
